export type PerformanceStage = 'frame' | 'preprocess' | 'ocr' | 'ocr-initialize'
export type DiagnosticError = 'capture-error' | 'frame-error' | 'image-error' | 'ocr-timeout' | 'ocr-engine-error' | 'ocr-initialize-error' | 'ocr-unknown-error' | 'cancelled'
type Counter = 'ocrInitializations' | 'ocrResets' | 'unchangedSkips' | 'recoveryBackoffs'
interface Measurement { at: number; ms: number }
interface Timing { count: number; totalMs: number; maxMs: number; recent: Measurement[] }
export interface CapturePerformanceInfo {
  status: string
  requestedFpsLimit: number
  reportedFps: number | null
  width: number | null
  height: number | null
  surface: string | null
}

const WINDOW_MS = 60_000
const MAX_SAMPLES = 240
const MAX_EVENTS = 40
const stages: PerformanceStage[] = ['frame', 'preprocess', 'ocr', 'ocr-initialize']

/** Bounded, in-memory metadata only. No images, recognized text, titles or URLs. */
export class PerformanceDiagnostics {
  private startedAt = Date.now()
  private timings = new Map<PerformanceStage, Timing>()
  private counters: Record<Counter, number> = { ocrInitializations: 0, ocrResets: 0, unchangedSkips: 0, recoveryBackoffs: 0 }
  private errorCounts: Partial<Record<DiagnosticError, number>> = {}
  private events: { at: string; code: DiagnosticError; retryInMs: number }[] = []
  private captureReader: (() => CapturePerformanceInfo) | null = null
  private processingIntervalMs: number | null = null

  setCaptureReader(reader: () => CapturePerformanceInfo): void { this.captureReader = reader }
  setProcessingInterval(intervalMs: number | null): void { this.processingIntervalMs = intervalMs }
  increment(counter: Counter): void { this.counters[counter] += 1 }

  measure(stage: PerformanceStage, ms: number): void {
    if (!Number.isFinite(ms) || ms < 0) return
    const timing = this.timings.get(stage) ?? { count: 0, totalMs: 0, maxMs: 0, recent: [] }
    timing.count += 1
    timing.totalMs += ms
    timing.maxMs = Math.max(timing.maxMs, ms)
    timing.recent.push({ at: Date.now(), ms })
    if (timing.recent.length > MAX_SAMPLES) timing.recent.shift()
    this.timings.set(stage, timing)
  }

  error(code: DiagnosticError, retryInMs = 0): void {
    this.errorCounts[code] = (this.errorCounts[code] ?? 0) + 1
    this.events.push({ at: new Date().toISOString(), code, retryInMs })
    if (this.events.length > MAX_EVENTS) this.events.shift()
  }

  clear(): void {
    this.startedAt = Date.now()
    this.timings.clear()
    this.counters = { ocrInitializations: 0, ocrResets: 0, unchangedSkips: 0, recoveryBackoffs: 0 }
    this.errorCounts = {}
    this.events = []
  }

  snapshot() {
    const now = Date.now()
    let capture: CapturePerformanceInfo | null = null
    try { capture = this.captureReader?.() ?? null } catch { /* closed or unavailable track */ }
    const timings = Object.fromEntries(stages.map(stage => {
      const timing = this.timings.get(stage)
      const recent = (timing?.recent ?? []).filter(sample => now - sample.at <= WINDOW_MS)
      const sorted = recent.map(sample => sample.ms).sort((a, b) => a - b)
      return [stage, {
        count: timing?.count ?? 0,
        averageMs: timing?.count ? round(timing.totalMs / timing.count) : null,
        maxMs: timing?.count ? round(timing.maxMs) : null,
        recentSampleCount: recent.length,
        recentP95Ms: sorted.length ? round(sorted[Math.ceil(sorted.length * .95) - 1]!) : null,
      }]
    }))
    return {
      schemaVersion: 1,
      startedAt: new Date(this.startedAt).toISOString(),
      generatedAt: new Date(now).toISOString(),
      durationSeconds: round((now - this.startedAt) / 1_000),
      environment: { userAgent: navigator.userAgent, hardwareConcurrency: navigator.hardwareConcurrency ?? null, visibility: document.visibilityState },
      capture,
      processingIntervalMs: this.processingIntervalMs,
      timings,
      counters: { ...this.counters },
      errorCounts: { ...this.errorCounts },
      recentErrors: this.events.map(event => ({ ...event })),
      limits: { recentWindowSeconds: WINDOW_MS / 1_000, maxSamplesPerStage: MAX_SAMPLES, maxRecentErrors: MAX_EVENTS },
    }
  }
}

function round(value: number): number { return Math.round(value * 100) / 100 }
export const performanceDiagnostics = new PerformanceDiagnostics()
